import type { InteractionHost } from "../interaction_host";
import { ModeHandler } from "./base";

// # Drag Zoom Mode
//
// Zooming by holding Ctrl+Middle mouse button and dragging vertically.
// Drag up to zoom in, drag down to zoom out.
// Uses Pointer Lock to hide the cursor during the drag and restore it on release.
//
export class DragZoom extends ModeHandler {
  // Accumulated vertical movement in pixels (from pointer lock movementY)
  private cumulativeDeltaY = 0;
  // Bound mousemove handler for cleanup
  private readonly onMouseMove: (e: MouseEvent) => void;

  constructor(
    readonly editor: InteractionHost,
    // Scale at mode entry
    readonly initialScale: number,
    // Schematic-space position at mode entry (zoom anchor point)
    readonly anchorSchX: number,
    readonly anchorSchY: number,
    // Page-space offset at mode entry
    readonly initialOffsetX: number,
    readonly initialOffsetY: number,
    // Mode to restore on exit (defaults to idle if not provided)
    private readonly returnMode?: ModeHandler
  ) {
    super();

    // Register our own mousemove listener to capture movementY.
    // The base handleMouseMove() receives no event argument,
    // so we need a direct listener to access movementY.
    this.onMouseMove = (e: MouseEvent) => {
      this.cumulativeDeltaY += e.movementY;
      this.applyZoom();
    };
    document.addEventListener("mousemove", this.onMouseMove);
  }

  // returnMode: if provided, exit() restores this mode instead of going idle.
  static start(editor: InteractionHost, returnMode?: ModeHandler) {
    const { mousePos } = editor.uiState;
    const { schToPage } = editor.canvas.worldTransforms;

    // Request pointer lock to hide cursor and get raw movement deltas.
    // If this fails (e.g. browser policy), zoom still works — cursor just won't be hidden.
    editor.parentDomElement.requestPointerLock();

    return new DragZoom(
      editor,
      schToPage.scale,
      mousePos.sch.x,
      mousePos.sch.y,
      schToPage.offset.x,
      schToPage.offset.y,
      returnMode
    );
  }

  override abort = () => {
    this.exit();
  };
  override handleMouseUp = () => {
    this.exit();
  };
  override handleMouseDown = (_: MouseEvent) => {};
  override handleDoubleClick = () => {};
  override handleKey = (_: KeyboardEvent) => {};

  // The editor's handleMouseMove calls this, but zoom is driven by our custom listener above.
  override handleMouseMove = () => {};

  // Apply zoom based on cumulative vertical movement.
  // Each pixel of vertical movement applies a small scale change.
  private applyZoom = () => {
    const { editor, initialScale, anchorSchX, anchorSchY, cumulativeDeltaY } =
      this;

    // Scale factor: ~0.5% per pixel of drag
    const f = Math.pow(1.005, -cumulativeDeltaY);
    const newScale = initialScale * f;

    // Recompute offset so the anchor point stays fixed on screen.
    // Same math as the wheel zoom: schToPage(anchor) should remain constant.
    const offset = {
      x: this.initialOffsetX + (1 - f) * initialScale * anchorSchX,
      y: this.initialOffsetY - (1 - f) * initialScale * anchorSchY, // y-inversion
    };

    editor.canvas.updateWorldTransforms(
      { scale: newScale, offset },
      "schToPage"
    );
  };

  exit = () => {
    document.removeEventListener("mousemove", this.onMouseMove);
    document.exitPointerLock();
    if (this.returnMode) {
      this.editor.setMode(this.returnMode);
    } else {
      this.editor.goUiIdle();
    }
  };
}
